import { Controller } from "stimulus";
import Swal from "sweetalert2";

// Manages the file-attachment area of a ticket reply form. It keeps the chosen files
// in a DataTransfer so the user can review them and remove any before submitting, then
// mirrors that list back onto the real <input type="file"> so the form still uploads
// through the standard multipart params (note[attachments][]). Files above the server
// limit (AttachmentSizeLimit, passed as values by the form) are left out with a warning.
export default class extends Controller {
  static targets = ["input", "list"];
  static values = { maxFileSize: Number, maxFileSizeMessage: String };

  connect() {
    this.transfer = new DataTransfer();
  }

  // Merges the newly picked files into the tracked list, avoiding duplicates by name+size.
  add() {
    const existing = new Set(
      Array.from(this.transfer.files).map((file) => `${file.name}:${file.size}`)
    );
    const rejected = [];

    Array.from(this.inputTarget.files).forEach((file) => {
      if (this.oversized(file)) {
        rejected.push(file);
        return;
      }

      const key = `${file.name}:${file.size}`;
      if (!existing.has(key)) {
        this.transfer.items.add(file);
        existing.add(key);
      }
    });

    this.syncInput();
    this.render();
    this.warnRejected(rejected);
  }

  oversized(file) {
    return this.maxFileSizeValue > 0 && file.size > this.maxFileSizeValue;
  }

  warnRejected(rejected) {
    if (!rejected.length) return;
    const names = rejected.map((file) => file.name).join(", ");
    Swal.fire({ icon: "warning", text: this.maxFileSizeMessageValue.replace("{{filename}}", names) });
  }

  remove(event) {
    const index = Number(event.currentTarget.dataset.index);
    const kept = Array.from(this.transfer.files).filter((_file, position) => position !== index);

    this.transfer = new DataTransfer();
    kept.forEach((file) => this.transfer.items.add(file));

    this.syncInput();
    this.render();
  }

  syncInput() {
    this.inputTarget.files = this.transfer.files;
  }

  render() {
    if (!this.hasListTarget) return;

    const files = Array.from(this.transfer.files);
    this.listTarget.classList.toggle("hidden", files.length === 0);
    this.listTarget.innerHTML = "";

    files.forEach((file, index) => {
      const chip = document.createElement("span");
      chip.className =
        "inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-700";
      chip.innerHTML = `
        <i class="fas fa-paperclip text-gray-400"></i>
        <span class="max-w-[12rem] truncate">${this.escape(file.name)}</span>
        <span class="text-gray-400">${this.humanSize(file.size)}</span>
        <button type="button" data-action="ticket-reply-attachments#remove" data-index="${index}"
                class="inline-flex items-center justify-center w-4 h-4 text-gray-400 hover:text-rose-600" title="Remover">
          <i class="fas fa-times text-[10px]"></i>
        </button>
      `;
      this.listTarget.appendChild(chip);
    });
  }

  escape(value) {
    const node = document.createElement("div");
    node.textContent = value;
    return node.innerHTML;
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB"];
    let size = bytes / 1024;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024;
      unit += 1;
    }
    return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
  }
}
